import type { DragInfo, WorkbenchMaterial } from '../../types';

/**
 * Simple rectangle containment
 */
export function isPointInRect(point: { x: number; y: number }, rect: DOMRect): boolean {
  return point.x >= rect.left && point.x <= rect.right && point.y >= rect.top && point.y <= rect.bottom;
}

/**
 * Map a screen point (mouse) into world coordinates centered for item placement.
 * Uses the current scroll and zoom from the scroll container.
 * Positions are snapped to integers to avoid subpixel blur.
 */
export function screenToWorldCentered(
  point: { x: number; y: number },
  containerRect: DOMRect,
  sc: HTMLDivElement,
  zoomOverride?: number,
  itemSize: number = 90
): { x: number; y: number } {
  const zoom = zoomOverride ?? 1;
  const mouseOnScaledContentX = point.x - containerRect.left + sc.scrollLeft;
  const mouseOnScaledContentY = point.y - containerRect.top + sc.scrollTop;
  const x = mouseOnScaledContentX / zoom - itemSize / 2;
  const y = mouseOnScaledContentY / zoom - itemSize / 2;
  return { x: Math.round(x), y: Math.round(y) };
}

/**
 * After dragging a workbench item, compute its new position, or determine if it should be deleted.
 * Returns the updated array or null if unchanged.
 * New positions are snapped to integers for crisp rendering.
 */
export function applyDropMoveAndMaybeDelete(
  currentMaterials: WorkbenchMaterial[],
  instanceId: string,
  info: DragInfo,
  sc: HTMLDivElement | null,
  zoom: number,
  itemSize: number
): WorkbenchMaterial[] | null {
  const itemBeingDragged = currentMaterials.find(i => i.instanceId === instanceId);
  if (!itemBeingDragged) return null;

  const rawX = itemBeingDragged.x + info.offset.x / zoom;
  const rawY = itemBeingDragged.y + info.offset.y / zoom;

  // Off-viewport deletion rule with margin
  if (sc) {
    const viewX = rawX * zoom - sc.scrollLeft;
    const viewY = rawY * zoom - sc.scrollTop;
    const itemSizeOnScreen = itemSize * zoom;
    const marginOut = itemSizeOnScreen * 0.75;

    if (
      viewX < -marginOut ||
      viewX > sc.clientWidth - itemSizeOnScreen + marginOut ||
      viewY < -marginOut ||
      viewY > sc.clientHeight - itemSizeOnScreen + marginOut
    ) {
      return currentMaterials.filter(i => i.instanceId !== instanceId);
    }
  }

  const newX = Math.round(rawX);
  const newY = Math.round(rawY);

  // Update position
  return currentMaterials.map(item =>
    item.instanceId === instanceId ? { ...item, x: newX, y: newY } : item
  );
}
